import type { HaulerTemplateDefinition } from './HaulerTemplateDefinition'
import { HaulerRequiredWhenCombinatorEnum } from '../enums/HaulerRequiredWhenCombinatorEnum'
import { HaulerRequiredWhenOpEnum } from '../enums/HaulerRequiredWhenOpEnum'
import { HaulerTemplateSectionEnum } from '../enums/HaulerTemplateSectionEnum'

// Mirrored in the mobile app's apps/totalmilk/hauler-mobile/src/template/seeds.ts (MILK_TEMPLATE) — no mobile entry point exports it; keep in sync if it changes.
export const MILK_TEMPLATE: HaulerTemplateDefinition = {
  // Sentinel — overwritten with generateId() when created/migrated; the read-path
  // MILK_TEMPLATE fallback (ResolveHaulerTemplate) returns it unresolved.
  id: '',
  code: 'milk',
  name: 'Milk Template',
  weightUnit: 'lbs',
  labels: {
    ticketSingular: 'Ticket',
    ticketPlural: 'Tickets',
    pickupSingular: 'Pickup',
    pickupPlural: 'Pickups',
    deliverySingular: 'Delivery',
    deliveryPlural: 'Deliveries',
    producerTypeLabel: 'Milk Producer',
    plantTypeLabel: 'Plant',
  },
  fields: [
    // Ticket section
    {
      key: 'ticketDate',
      label: 'Ticket Date',
      required: true,
      webVisible: true,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 0,
    },
    {
      key: 'ticketDrivers',
      label: 'Drivers',
      required: true,
      webVisible: true,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 1,
    },
    {
      key: 'ticketHauler',
      label: 'Hauler',
      required: false,
      webVisible: true,
      mobileVisible: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 2,
    },
    {
      key: 'ticketRemarks',
      label: 'Ticket Remarks',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 3,
    },
    {
      key: 'ticketManifestNo',
      label: 'Manifest No',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 4,
    },
    {
      key: 'ticketTrailerNo',
      label: 'Trailer No',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 5,
    },
    {
      key: 'ticketDriverOrHelperName',
      label: 'Driver/Helper Name',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 6,
    },
    {
      key: 'ticketSealNo',
      label: 'Seal No',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 7,
    },
    {
      key: 'ticketStartingWeight',
      label: 'Starting Weight',
      unit: 'lbs',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 8,
    },
    {
      key: 'ticketSamplerLicenseNo',
      label: 'Sampler License No.',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 9,
    },
    {
      key: 'ticketLicenseNoExpirationDate',
      label: 'License Expiration',
      required: false,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 10,
    },
    {
      key: 'ticketDriverSignature',
      label: 'Driver signature',
      required: true,
      section: HaulerTemplateSectionEnum.Ticket,
      order: 11,
    },
    // Pickup section
    {
      key: 'pickupQuantity',
      label: 'Quantity',
      unit: 'lbs',
      required: true,
      webVisible: true,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 1,
    },
    {
      key: 'pickupGaugeRod',
      label: 'Gauge Rod',
      required: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 2,
    },
    {
      key: 'pickupGradeType',
      label: 'Grade Type',
      required: true,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 3,
    },
    {
      key: 'pickupExtraGradeType',
      label: 'Extra Grade Type',
      required: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 4,
    },
    {
      key: 'pickupDate',
      label: 'Pickup Date',
      required: true,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 5,
      validation: { dateWindow: { other: 'ticketDate', daysBefore: 3, daysAfter: 3 } },
    },
    {
      key: 'pickupUnitId',
      label: 'Unit ID',
      required: true,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 6,
    },
    {
      key: 'pickupTemperature',
      label: 'Temperature',
      unit: '°F',
      required: true,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 7,
      validation: { min: 0, max: 50 },
    },
    {
      key: 'pickupRemarks',
      label: 'Pickup Remarks',
      required: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 8,
    },
    {
      key: 'pickupBarcode',
      label: 'Barcode',
      required: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 9,
    },
    {
      key: 'pickupCpd',
      label: 'CPD',
      required: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 10,
    },
    {
      key: 'pickupNumberOfMilkings',
      label: 'No. of Milkings',
      required: false,
      section: HaulerTemplateSectionEnum.Pickup,
      order: 11,
    },
    // Delivery section
    {
      key: 'deliveryQuantity',
      label: 'Quantity Delivered',
      unit: 'lbs',
      required: true,
      webVisible: true,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 1,
    },
    {
      key: 'deliveryDate',
      label: 'Delivery Date',
      required: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 2,
      validation: { dateWindow: { other: 'ticketDate', daysBefore: 3, daysAfter: 3 } },
    },
    {
      key: 'deliveryBtu',
      label: 'BTU',
      required: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 3,
    },
    {
      key: 'deliverySilo',
      label: 'Silo',
      required: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 4,
    },
    {
      key: 'deliveryDrugTest',
      label: 'Drug Test',
      required: false,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 5,
    },
    {
      key: 'deliveryDrugResult',
      label: 'Drug Result',
      required: false,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 6,
    },
    {
      key: 'deliveryRemarks',
      label: 'Delivery Remarks',
      required: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 7,
      validation: {
        requiredWhen: [
          {
            field: 'deliveryDrugResult',
            combinator: HaulerRequiredWhenCombinatorEnum.Or,
            comparisons: [{ op: HaulerRequiredWhenOpEnum.Contains, value: 'POS' }],
          },
        ],
      },
    },
    {
      key: 'deliveryGrossWeight',
      label: 'Gross Weight',
      unit: 'lbs',
      required: false,
      webVisible: false,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 8,
    },
    {
      key: 'deliveryTareWeight',
      label: 'Tare Weight',
      unit: 'lbs',
      required: false,
      webVisible: false,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 9,
    },
    {
      key: 'deliveryNetWeight',
      label: 'Net Weight',
      unit: 'lbs',
      required: false,
      webVisible: false,
      mobileVisible: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 10,
    },
    {
      key: 'deliverySignature',
      label: 'Delivery Signature',
      required: true,
      section: HaulerTemplateSectionEnum.Delivery,
      order: 11,
    },
    // Customer section — milk producer / plant profile fields
    {
      key: 'customerUnitIds',
      label: 'Unit IDs',
      required: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 1,
    },
    {
      key: 'customerBtus',
      label: 'BTUs',
      required: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 2,
    },
    {
      key: 'customerSilos',
      label: 'Silos',
      required: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 3,
    },
    {
      key: 'customerSyncInfo',
      label: 'TMMS Sync Info',
      required: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 4,
    },
    {
      key: 'customerPlantCode',
      label: 'Plant Code',
      required: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 5,
    },
    {
      key: 'customerReportId',
      label: 'Report ID',
      required: false,
      section: HaulerTemplateSectionEnum.Customer,
      order: 6,
    },
    // Driver section — milk hauler driver profile fields
    {
      key: 'driverSamplerLicenseNo',
      label: 'Sampler License No.',
      required: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 1,
    },
    {
      key: 'driverLicenseExpirationDate',
      label: 'License Expiry',
      required: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 2,
    },
    {
      key: 'driverTicketHaulerName',
      label: 'Hauler Name',
      required: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 3,
    },
    {
      key: 'driverTicketHaulerAddress',
      label: 'Hauler Address',
      required: false,
      section: HaulerTemplateSectionEnum.Driver,
      order: 4,
    },
  ],
}
